import { clsx } from 'clsx';
import Autoplay from 'embla-carousel-autoplay';
import useEmblaCarousel from 'embla-carousel-react';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';
import React, { ComponentPropsWithRef, useMemo } from 'react';
import { IconButton } from '../button/icon-button.tsx';
import { ArrowLeftIcon } from '../icons/react/arrow-left.tsx';
import { ArrowRightIcon } from '../icons/react/arrow-right.tsx';
import { composeEventHandlers } from '../utils/compose-event-handlers.ts';
import { CarouselContext } from './carousel-context.tsx';
import { CarouselTrigger } from './carousel-trigger.tsx';
import { CarouselApi, CarouselProps } from './carousel-type.ts';
import {
  carouselButton,
  carouselContent,
  carouselContentContainer,
  carouselContentVariants,
  carouselDot,
  carouselDots,
  carouselItem,
} from './carousel.css.ts';
import { useCarouselTrigger } from './use-carousel-trigger.ts';
import { useCarousel } from './use-carousel.ts';

const Carousel = function Carousel({
  ref,
  orientation = 'horizontal',
  opts,
  setApi,
  autoplay = false,
  loop = false,
  delay = 5000,
  className,
  children,
  ...props
}: CarouselProps & ComponentPropsWithRef<'div'>) {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y',
      loop,
    },
    [Autoplay({ delay, active: autoplay }), WheelGesturesPlugin()],
  );

  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (!api) {
      return;
    }

    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev();
  }, [api]);

  const scrollNext = React.useCallback(() => {
    api?.scrollNext();
  }, [api]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext],
  );

  React.useEffect(() => {
    if (!api || !setApi) {
      return;
    }

    setApi(api);
  }, [api, setApi]);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    onSelect(api);
    api.on('reInit', onSelect);
    api.on('select', onSelect);

    return () => {
      api?.off('select', onSelect);
    };
  }, [api, onSelect]);

  const value = useMemo(
    () => ({
      carouselRef,
      api: api,
      opts,
      orientation:
        orientation || (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    }),
    [
      carouselRef,
      api,
      opts,
      orientation,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    ],
  );

  return (
    <CarouselContext.Provider value={value}>
      <div
        ref={ref}
        onKeyDownCapture={handleKeyDown}
        className={className}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
};

const CarouselContent = function CarouselContent({
  ref,
  className,
  containerClassName,
  ...props
}: ComponentPropsWithRef<'div'> & {
  containerClassName?: string;
}) {
  const { carouselRef, orientation } = useCarousel();

  return (
    <div
      ref={carouselRef}
      className={clsx(carouselContentContainer, containerClassName)}
    >
      <div
        ref={ref}
        className={clsx(
          carouselContent,
          orientation && carouselContentVariants[orientation],
          className,
        )}
        {...props}
      />
    </div>
  );
};

const CarouselItem = function CarouselItem({
  ref,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={clsx(carouselItem, className)}
      {...props}
    />
  );
};

const CarouselPrevious = function CarouselPrevious({
  ref,
  className,
  variant = 'secondary',
  size = 'small',
  onClick,
  children,
  ...props
}: React.ComponentProps<typeof IconButton> & {
  ref?: React.Ref<HTMLButtonElement>;
}) {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <IconButton
      ref={ref}
      variant={variant}
      size={size}
      className={clsx(carouselButton, className)}
      disabled={!canScrollPrev}
      onClick={composeEventHandlers(onClick, scrollPrev)}
      {...props}
    >
      {children || <ArrowLeftIcon />}
    </IconButton>
  );
};

const CarouselNext = function CarouselNext({
  ref,
  className,
  variant = 'secondary',
  size = 'small',
  onClick,
  children,
  ...props
}: React.ComponentProps<typeof IconButton> & {
  ref?: React.Ref<HTMLButtonElement>;
}) {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <IconButton
      ref={ref}
      variant={variant}
      size={size}
      className={clsx(carouselButton, className)}
      disabled={!canScrollNext}
      onClick={composeEventHandlers(onClick, scrollNext)}
      {...props}
    >
      {children || <ArrowRightIcon />}
    </IconButton>
  );
};

const CarouselDots = function CarouselDots({
  ref,
  className,
}: React.ComponentPropsWithRef<'div'> & {
  ref?: React.Ref<HTMLDivElement>;
}) {
  const { api } = useCarousel();

  const { scrollSnaps } = useCarouselTrigger(api);

  return (
    <div ref={ref} className={carouselDots}>
      {scrollSnaps.map((_, index) => (
        <CarouselTrigger
          key={index}
          index={index}
          className={clsx(carouselDot, className)}
        />
      ))}
    </div>
  );
};

export {
  Carousel,
  CarouselContent,
  CarouselDots,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
};
